import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Box,
    Container,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Input
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../../resources/strings/StringKeys';
import { ToastType, useToast } from '../../../../../context/toast/ToastContext';
import { useLoadingContext } from '../../../../../context/loading/LoadingContext';
import { useUserContext } from '../../../../../context/user_context/UserContext';
import Logo from '../../../../../resources/images/logo/bizismart.png';
import { IconX } from '@tabler/icons-react';
import { addPerformanceReviewTemplate } from '../../../../../repository/PerformanceReviewDAO';

// Predefined multiple-choice performance statements (Performance section)
const GENERAL_PERFORMANCE_QUESTIONS = [
    { id: 'gp1', textKey: StringKeys.PERFORMANCE_QUESTION_1 },
    { id: 'gp2', textKey: StringKeys.PERFORMANCE_QUESTION_2 },
    { id: 'gp3', textKey: StringKeys.PERFORMANCE_QUESTION_3 },
    { id: 'gp4', textKey: StringKeys.PERFORMANCE_QUESTION_4 },
    { id: 'gp5', textKey: StringKeys.PERFORMANCE_QUESTION_5 },
];

// Predefined free-form questions for Goals section
const GOALS_QUESTIONS = [
    { id: 'gq1', textKey: StringKeys.GOALS_QUESTION_1 },
    { id: 'gq2', textKey: StringKeys.GOALS_QUESTION_2 },
    { id: 'gq3', textKey: StringKeys.GOALS_QUESTION_3 },
];

// Predefined free-form questions for Feedback section
const FEEDBACK_QUESTIONS = [
    { id: 'fq1', textKey: StringKeys.FEEDBACK_QUESTION_1 },
    { id: 'fq2', textKey: StringKeys.FEEDBACK_QUESTION_2 },
    { id: 'fq3', textKey: StringKeys.FEEDBACK_QUESTION_3 },
];

export const CreateReviewTemplateModal = ({ open, onHide }) => {

    const { t } = useTranslation();
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [customQuestionInput, setCustomQuestionInput] = useState('');
    const [customQuestionSection, setCustomQuestionSection] = useState('performance')
    const [availableGeneralPerformance, setAvailableGeneralPerformance] = useState(GENERAL_PERFORMANCE_QUESTIONS);
    const [availableGoals, setAvailableGoals] = useState(GOALS_QUESTIONS);
    const [availableFeedback, setAvailableFeedback] = useState(FEEDBACK_QUESTIONS);
    const [templateName, setTemplateName] = useState('')
    const [templateNameError, setTemplateNameError] = useState(false);
    const { setLoadingData } = useLoadingContext();
    const { firebaseUser } = useUserContext();
    const { showToast } = useToast();


    // Helper to determine question type based on section
    const getQuestionTypeBySection = (section) => {
        if (section === 'performance') return 'multipleChoice';
        return 'freeForm'; // goals and feedback are freeForm
    };

    // Add general performance question
    const handleAddGeneralPerformanceQuestion = (question) => {
        const newQ = { 
            ...question, 
            section: 'performance', 
            type: 'multipleChoice',
            text: t(question.textKey),  // Store the translated text
            textKey: question.textKey   // Store the key for future reference
        };
        setSelectedQuestions(prev => [...prev, newQ]);
        setAvailableGeneralPerformance(prev => prev.filter(q => q.id !== question.id));
    };

    // Add goals question
    const handleAddGoalsQuestion = (question) => {
        const newQuestion = { 
            ...question, 
            section: 'goals', 
            type: 'freeForm',
            text: t(question.textKey),
            textKey: question.textKey
        };
        setSelectedQuestions(prev => [...prev, newQuestion]);
        setAvailableGoals(prev => prev.filter(q => q.id !== question.id));
    };

    // Add feedback question
    const handleAddFeedbackQuestion = (question) => {
        const newQ = { 
            ...question, 
            section: 'feedback', 
            type: 'freeForm',
            text: t(question.textKey),
            textKey: question.textKey
        };
        setSelectedQuestions(prev => [...prev, newQ]);
        setAvailableFeedback(prev => prev.filter(q => q.id !== question.id));
    };

    // Add custom question
    const handleAddCustomQuestion = () => {
        if (customQuestionInput.trim()) {
            const section = customQuestionSection;
            const newQuestion = {
                id: `custom_${Date.now()}`,
                text: customQuestionInput.trim(),
                isCustom: true,
                section,
                type: getQuestionTypeBySection(section),
                // For custom questions, store the text directly as it's user-entered
                textKey: null
            };
            setSelectedQuestions(prev => [...prev, newQuestion]);
            setCustomQuestionInput(''); 
        }
    };

    // Remove question from selected
    const handleRemoveQuestion = (questionId) => {
        const removedQuestion = selectedQuestions.find(q => q.id === questionId);
        setSelectedQuestions(prev => prev.filter(q => q.id !== questionId));

        // If it was a predefined question, return it to the pool
        if (removedQuestion && !removedQuestion.isCustom) {
            if (removedQuestion.section === 'performance') {
                setAvailableGeneralPerformance(prev => [...prev, removedQuestion]);
            } else if (removedQuestion.section === 'goals') {
                setAvailableGoals(prev => [...prev, removedQuestion]);
            } else if (removedQuestion.section === 'feedback') {
                setAvailableFeedback(prev => [...prev, removedQuestion]);
            }
        }
    };

    // Sort selected questions by section: performance → goals → feedback
    const sortQuestions = (questions) => {
        const order = { performance: 1, goals: 2, feedback: 3 };
        return [...questions].sort((a, b) => order[a.section] - order[b.section]);
    };

    const sortedSelectedQuestions = sortQuestions(selectedQuestions);

    const checkValidations = () => {
        let valid = true;
        if (!templateName.trim()) {
            setTemplateNameError(true);
            valid = false;
        }
        if (selectedQuestions.length < 1) {
            showToast(t(StringKeys.AT_LEAST_ONE_QUESTION_MUST_BE_SELECTED_TO_CREATE_TEMPLATE), ToastType.DANGER)
            valid = false;
        }
        return valid;
    }

    const handleSubmit = async () => {
        if(!checkValidations()) return;
    
        try {
            setLoadingData(true);
        
            const template = {
                name: templateName,
                questions: sortedSelectedQuestions,
            };
            const result = await addPerformanceReviewTemplate(firebaseUser.organization, firebaseUser.uid, template);
    
            if (result.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
                resetForm();
                onHide();
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
        } catch (error) {
            console.log(error)
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    const resetForm = () => {
        setSelectedQuestions([])
        setCustomQuestionInput('')
        setCustomQuestionSection('performance')
        setAvailableGeneralPerformance(GENERAL_PERFORMANCE_QUESTIONS)
        setAvailableGoals(GOALS_QUESTIONS)
        setAvailableFeedback(FEEDBACK_QUESTIONS)
        setTemplateName('')
        setTemplateNameError(false)
    }

    return (
        <Dialog 
            open={open} 
            onClose={() => {
                resetForm();
                onHide();
            }}            
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { 
                    height: '90vh', 
                    maxHeight: '90vh',
                    '@media (max-width: 600px)': {
                        height: '95vh',
                        maxHeight: '95vh'
                    }
                } 
            }}
        >
                <DialogTitle>
                <img src={Logo} alt="logo" style={{ height: '30px', marginRight:"20px" }} />
                {t(StringKeys.CREATE_REVIEW_TEMPLATE)}
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        onHide()
                        resetForm()
                    }}
                    sx={{
                    
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <IconX />
                </IconButton>
            </DialogTitle>
            <DialogContent 
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    overflow: 'auto',
                    p: 2 
                }}
                dividers
            >
                <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 } }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="template-name"
                        label={t(StringKeys.TEMPLATE_NAME)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={templateName}
                        onChange={(e) => {
                            setTemplateName(e.target.value);
                        }}
                        error={templateNameError}
                        helperText={templateNameError ? t(StringKeys.TEMPLATE_NAME_IS_REQUIRED) : ""}
                    />
                    
                    {/* Selected Questions Section */}
                    <Typography 
                        variant="h6" 
                        gutterBottom
                    >
                        {t(StringKeys.SELECTED_QUESTIONS)} ({sortedSelectedQuestions.length})
                    </Typography>
                    <Paper 
                        variant="outlined" 
                        sx={{ 
                            mb: 3, 
                            p: 2, 
                            minHeight: '100px',
                            maxHeight: '250px', 
                            overflow: 'auto' 
                        }}
                    >
                        {sortedSelectedQuestions.length > 0 ? (
                            <List dense>
                                {sortedSelectedQuestions.map((q, index) => {
                                    let secondaryLabel = q.isCustom ? t(StringKeys.CUSTOM_QUESTION) : t(StringKeys.PREMADE_QUESTION);
                                    // Append the section name to indicate what category it belongs to
                                    if (q.section === 'performance') {
                                        secondaryLabel += ` - ${t(StringKeys.GENERAL_PERFORMANCE)}`;
                                    } else if (q.section === 'goals') {
                                        secondaryLabel += ` - ${t(StringKeys.GOALS)}`;
                                    } else if (q.section === 'feedback') {
                                        secondaryLabel += ` - ${t(StringKeys.FEEDBACK)}`;
                                    }

                                    return (
                                        <ListItem 
                                            key={q.id} 
                                            secondaryAction={
                                                <IconButton 
                                                    edge="end" 
                                                    onClick={() => handleRemoveQuestion(q.id)}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText 
                                                primary={`${index + 1}. ${q.text}`}
                                                secondary={secondaryLabel}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        ) : (
                            <Typography 
                                color="textSecondary" 
                                align="center" 
                                sx={{ py: 2 }}
                            >
                                {t(StringKeys.NO_QUESTIONS_SELECTED)}
                            </Typography>
                        )}
                    </Paper>

                    {/* General Performance (Multiple Choice) */}
                    <Typography variant="h6" gutterBottom>
                        {t(StringKeys.GENERAL_PERFORMANCE)} ({t(StringKeys.MULTIPLE_CHOICE)})
                    </Typography>
                    <Paper variant="outlined" sx={{ mb: 3 }}>
                        {availableGeneralPerformance.length > 0 ? (
                            <List dense>
                                {availableGeneralPerformance.map((q) => (
                                    <ListItem 
                                        key={q.id}
                                        secondaryAction={
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => handleAddGeneralPerformanceQuestion(q)}
                                            >
                                                <Add />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={t(q.textKey)} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography 
                                color="textSecondary" 
                                align="center" 
                                sx={{ py: 2 }}
                            >
                                {t(StringKeys.NO_MORE_PREMADE_QUESTIONS)}
                            </Typography>
                        )}
                    </Paper>

                    {/* Goals (Free Form) */}
                    <Typography variant="h6" gutterBottom>
                        {t(StringKeys.GOALS)} ({t(StringKeys.FREE_FORM)})
                    </Typography>
                    <Paper variant="outlined" sx={{ mb: 3 }}>
                        {availableGoals.length > 0 ? (
                            <List dense>
                                {availableGoals.map((q) => (
                                    <ListItem 
                                        key={q.id}
                                        secondaryAction={
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => handleAddGoalsQuestion(q)}
                                            >
                                                <Add />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={t(q.textKey)} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography 
                                color="textSecondary" 
                                align="center" 
                                sx={{ py: 2 }}
                            >
                                {t(StringKeys.NO_MORE_PREMADE_QUESTIONS)}
                            </Typography>
                        )}
                    </Paper>

                    {/* Feedback (Free Form) */}
                    <Typography variant="h6" gutterBottom>
                        {t(StringKeys.FEEDBACK)} ({t(StringKeys.FREE_FORM)})
                    </Typography>
                    <Paper variant="outlined" sx={{ mb: 3 }}>
                        {availableFeedback.length > 0 ? (
                            <List dense>
                                {availableFeedback.map((q) => (
                                    <ListItem 
                                        key={q.id}
                                        secondaryAction={
                                            <IconButton 
                                                edge="end" 
                                                onClick={() => handleAddFeedbackQuestion(q)}
                                            >
                                                <Add />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={t(q.textKey)} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography 
                                color="textSecondary" 
                                align="center" 
                                sx={{ py: 2 }}
                            >
                                {t(StringKeys.NO_MORE_PREMADE_QUESTIONS)}
                            </Typography>
                        )}
                    </Paper>


                    {/* Custom Question Input */}
                    <Typography variant="h6" gutterBottom>
                        {t(StringKeys.CUSTOM_QUESTION)}
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 2, 
                        mb: 3 
                    }}>
                        <TextField
                            fullWidth
                            label={t(StringKeys.ADD_A_CUSTOM_QUESTION)}
                            value={customQuestionInput}
                            onChange={(e) => setCustomQuestionInput(e.target.value)}
                            variant="outlined"
                        />
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t(StringKeys.QUESTION_TYPE)}</FormLabel>
                            <RadioGroup
                                row
                                value={customQuestionSection}
                                onChange={(e) => setCustomQuestionSection(e.target.value)}
                            >
                                <FormControlLabel 
                                    value="performance" 
                                    control={<Radio />} 
                                    label={t(StringKeys.GENERAL_PERFORMANCE)} 
                                />
                                <FormControlLabel 
                                    value="goals" 
                                    control={<Radio />} 
                                    label={t(StringKeys.GOALS)} 
                                />
                                <FormControlLabel 
                                    value="feedback" 
                                    control={<Radio />} 
                                    label={t(StringKeys.FEEDBACK)} 
                                />
                            </RadioGroup>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={handleAddCustomQuestion}
                            disabled={!customQuestionInput.trim()}
                            startIcon={<Add />}
                        >
                            {t(StringKeys.ADD_QUESTION)}
                        </Button>
                    </Box>
                </Container>
            </DialogContent>
            <DialogActions>
            <Button
                    color="primary"
                    disabled={selectedQuestions.length === 0}
                    onClick={handleSubmit}
                >
                    {t(StringKeys.SUBMIT)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
